import { ZIndexes } from "@common/constants";
import { AnimalKind } from "@common/definitions/animals";
import { type ObstacleDefinition } from "@common/definitions/obstacles";
import { type ShadowLevel } from "./lightingLogic";

/*
    The shadows (0.98.6, owner decision, 1 November 2026: "Shadows high and low or OFF"): the parts without Pixi
    (tests/src/update0986.test.ts). Every thing standing under the sky throws a shadow of its own picture along the
    sun (managers/lighting.ts says which way and how long): how tall each kind of thing is, so its shadow has its
    length, and how a tall thing's shadow is swept (its picture drawn a few times from its foot to its shadow's end,
    the sweep of its height), so a tree's crown throws a long shape and not a disc floating beside it. See
    managers/shadowLook.ts for the drawing.
*/

/** how the shadows are drawn at each level */
export interface ShadowLook {
    /** the shadows' picture is this share of the screen's pixels each way (it's dark and soft: small is enough) */
    readonly scale: number
    /** their edges blurred (High): the sun's softness times this many pixels of the picture */
    readonly blur: number
    /** a tall thing's shadow swept from its foot to its end (High), or its picture put down once at the end (Low) */
    readonly sweep: boolean
    /** the buildings' roofs throw shadows too (Low keeps to the things on the ground and the people) */
    readonly roofs: boolean
}
export const SHADOW_LOOKS: Readonly<Record<Exclude<ShadowLevel, "off">, ShadowLook>> = Object.freeze({
    low: { scale: 0.5, blur: 0, sweep: false, roofs: false },
    high: { scale: 0.5, blur: 6, sweep: true, roofs: true }
});

/** heights (game units: a player is 2.25 round) */
export const HEIGHTS = Object.freeze({
    player: 1.9,
    /** knocked down: lying low */
    downed: 0.6,
    loot: 0.45,
    tree: 6,
    /** a building's ground floor roof; each floor over it adds `floor` */
    building: 5,
    floor: 2.6,
    /** a building's shadow never longer than this (a four-storey block's would cross the street at dusk) */
    buildingMost: 18,
    wall: 3,
    bush: 1.2,
    stone: 1.4,
    /** tables, chairs and the like (ZIndexes.ObstaclesLayer3: things a player walks past) */
    furniture: 1.3,
    /** crates, barrels, boxes, stoves, lockers: the ordinary things standing about */
    thing: 2.1,
    /** things drawn over the roofs (chimneys, the museum's dome's lantern) */
    tall: 3.5,
    car: 2.2
});

/** the animals' heights (a flier in the air has its own shadow: objects/animal.ts) */
export const ANIMAL_HEIGHTS: Readonly<Record<AnimalKind, number>> = Object.freeze({
    [AnimalKind.Hare]: 0.8,
    [AnimalKind.Spider]: 0.25,
    [AnimalKind.Mothman]: 2.4,
    [AnimalKind.Yeti]: 4,
    [AnimalKind.Rat]: 0.4
});

/** the cars (the streets' of 0.98.4, the parked ones, the prison's van): low and long */
const CARS = /^(street_(sedan|suv|hatch)|parked_car|police_van)$/;

/** each definition's height, worked out once (0.98.8: the shadows, the shapes and the lights asked for every obstacle's every frame) */
const casterHeights = new WeakMap<ObstacleDefinition, number>();

/**
 * how tall an obstacle stands (game units), from its definition: 0 for what throws no shadow (a thing lying flat on
 * the ground, a door, a window, a stair, something drawn as a colour or invisible, a residue)
 */
export function casterHeight(definition: ObstacleDefinition): number {
    let height = casterHeights.get(definition);
    if (height === undefined) casterHeights.set(definition, height = heightOf(definition));
    return height;
}

function heightOf(definition: ObstacleDefinition): number {
    if (definition.invisible || definition.isDoor || definition.isStair || definition.isWindow) return 0;
    const z = definition.zIndex ?? ZIndexes.ObstaclesLayer1;
    // (under the loot: a thing lying on the ground, a trap, a puddle, a fence drawn with the floor)
    if (z < ZIndexes.ObstaclesLayer1) return 0;
    // (drawn as a shape rather than a picture: a built wall, a painted patch of ground)
    if (definition.wall || (definition.graphics?.length && !definition.frames?.base)) return definition.wall ? HEIGHTS.wall : 0;
    if (definition.isTree) return HEIGHTS.tree;
    if (definition.material === "bush") return HEIGHTS.bush;
    // (nothing stops on it and it's drawn under the players: a pebble, a cobweb, a pallet, a rug; a table is walked
    // under and drawn over them, and stands)
    if (definition.noCollisions && z < ZIndexes.ObstaclesLayer3) return 0;
    if (definition.isWall) return HEIGHTS.wall;
    if (CARS.test(definition.idString)) return HEIGHTS.car;
    if (z >= ZIndexes.ObstaclesLayer5) return HEIGHTS.tall;
    if (z >= ZIndexes.ObstaclesLayer4) return HEIGHTS.tree;
    switch (definition.material) {
        case "stone": return HEIGHTS.stone;
        case "glass": return HEIGHTS.wall;
        default: break;
    }
    if (z >= ZIndexes.ObstaclesLayer3 && z < ZIndexes.ObstaclesLayer4) return HEIGHTS.furniture;
    return HEIGHTS.thing;
}

/** how high a building's roof is, by the floor it is (0 the ground, 2 the next, 4, 6: common/src/constants.ts `Layer`) */
export function buildingHeight(layer: number): number {
    return HEIGHTS.building + Math.max(0, layer / 2) * HEIGHTS.floor;
}

/** a shadow's length on the ground (game units) for a thing `height` tall under a sun of that `stretch` */
export function shadowLength(height: number, stretch: number, building = false): number {
    const length = height * stretch;
    return building ? Math.min(length, HEIGHTS.buildingMost) : length;
}

/**
 * where a thing's picture is put down for its shadow: each a share (0 its foot, 1 its shadow's end) of the way along
 * the sun. Swept (High): enough copies for a shadow `length` long of a picture `size` wide to join up (one every half
 * picture, two at least, six at most); otherwise the picture once at the end, where a hard sun throws it
 */
export function sweepStops(length: number, size: number, sweep: boolean): readonly number[] {
    if (!sweep || length <= 0) return ONCE;
    const copies = Math.min(6, Math.max(2, Math.ceil(length / Math.max(0.5, size * 0.5)) + 1));
    // (a count that isn't a number, from a size that isn't: no copies, as the loop that made them made none)
    return SWEEPS[copies] ?? NONE;
}

/**
 * the stops by their count, each made once and kept (0.98.8: an array was made for every shadow every frame): the
 * picture once, at the end; two to six copies along the way
 */
const ONCE: readonly number[] = Object.freeze([1]);
const NONE: readonly number[] = Object.freeze([]);
const SWEEPS: ReadonlyArray<readonly number[] | undefined> = (() => {
    const all: Array<readonly number[] | undefined> = [];
    for (let copies = 2; copies <= 6; copies++) {
        const stops: number[] = [];
        for (let i = 0; i < copies; i++) stops.push(i / (copies - 1));
        all[copies] = Object.freeze(stops);
    }
    return Object.freeze(all);
})();

/** the composite's place among a floor's things: over the ground's decals and the residues, under everything standing */
export const SHADOW_ZINDEX = ZIndexes.DeadObstacles + 0.5;

/**
 * the contact shade (0.98.7, the owner: "Contact darkening"): a soft dark band round the foot of everything standing,
 * wider and darker as the thing is taller: `band` game units past its hitbox, `alpha` its darkness there (the
 * composite's own `alpha` over it); nothing under `least` tall (a pebble, a rug)
 */
export const CONTACT = Object.freeze({ alpha: 0.6, least: 0.9 });

/**
 * the round things' contact discs (0.98.7's audit): each disc's soft edge is this share of its radius, one picture
 * each; a round thing's disc is its radius and its band across, drawn with the one whose edge is nearest the band's
 * share of it, so the shade is full at the thing's foot and gone a band out
 */
export const DISC_EDGES: readonly number[] = Object.freeze([0.08, 0.12, 0.16, 0.2, 0.25, 0.3, 0.36, 0.43, 0.5, 0.6, 0.7, 0.8, 0.9, 1]);

/** which of `DISC_EDGES` (its index) a round thing of `radius` with a contact band of `band` (game units) is drawn with */
export function discEdge(radius: number, band: number): number {
    const want = band / Math.max(1e-6, radius + band);
    let best = 0;
    for (let i = 1; i < DISC_EDGES.length; i++) {
        if (Math.abs(DISC_EDGES[i] - want) < Math.abs(DISC_EDGES[best] - want)) best = i;
    }
    return best;
}
export function contactBand(height: number): { band: number, alpha: number } {
    if (height < CONTACT.least) return { band: 0, alpha: 0 };
    const band = Math.min(1.1, 0.22 + 0.17 * height);
    const alpha = Math.min(1, 0.3 + 0.18 * height);
    return { band, alpha };
}

/**
 * the shadows' silhouette (a filter over the shadows' picture, WebGL): the pictures' shape in white, their colours
 * dropped, so the composite takes the sun's colour as its tint. GLSL ES 1.0 as Pixi rewrites it
 */
export const SILHOUETTE_FRAGMENT = /* glsl */`
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;

void main(void) {
    float a = texture(uTexture, vTextureCoord).a;
    finalColor = vec4(a, a, a, a);
}
`;
